import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  Shield, Search, RefreshCw, User, FileText, 
  AlertTriangle, Clock, Filter, Download
} from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Action type icons and colors
const ACTION_CONFIG = {
  login: { icon: User, color: 'text-green-600', bg: 'bg-green-100', label: 'Login' },
  logout: { icon: User, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Logout' },
  create_patient: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Criar Paciente' },
  update_patient: { icon: FileText, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Editar Paciente' },
  delete_patient: { icon: FileText, color: 'text-red-600', bg: 'bg-red-100', label: 'Deletar Paciente' },
  create_prescription: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Criar Receita' },
  update_prescription: { icon: FileText, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Editar Receita' },
  print_prescription: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Imprimir Receita' },
  renew_prescription: { icon: FileText, color: 'text-green-600', bg: 'bg-green-100', label: 'Renovar Receita' },
  create_document: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Criar Documento' },
  approve_user: { icon: User, color: 'text-green-600', bg: 'bg-green-100', label: 'Aprovar Usuário' },
  default: { icon: AlertTriangle, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Ação' }
};

export default function AuditLogs() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [uniqueActions, setUniqueActions] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      if (!['administrador', 'auditor'].includes(parsedUser.role)) {
        toast.error('Acesso negado');
        navigate('/');
        return;
      }
      setUser(parsedUser);
      loadLogs();
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/audit/logs?limit=500`, getAuthHeaders());
      const logsData = response.data.logs || response.data || [];
      setLogs(logsData);
      
      // Extract unique users and actions for filters
      const users = [...new Set(logsData.map(l => l.user_name).filter(Boolean))];
      const actions = [...new Set(logsData.map(l => l.action).filter(Boolean))];
      setUniqueUsers(users);
      setUniqueActions(actions);
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      // Create some mock data if endpoint doesn't exist
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = () => {
    const csv = [
      ['Data/Hora', 'Usuário', 'Ação', 'Tipo', 'Detalhes', 'IP'].join(','),
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toLocaleString('pt-BR'),
        log.user_name || '',
        log.action || '',
        log.entity_type || '',
        JSON.stringify(log.details || {}),
        log.ip_address || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = 
      !searchTerm ||
      log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.entity_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    const matchesUser = filterUser === 'all' || log.user_name === filterUser;
    
    return matchesSearch && matchesAction && matchesUser;
  });

  const getActionConfig = (action) => {
    return ACTION_CONFIG[action] || ACTION_CONFIG.default;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F7F8] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0F766E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7F8]">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#0F766E]" />
            <h1 className="text-2xl font-bold text-[#1F2933]">Logs de Auditoria</h1>
          </div>
          <p className="text-[#64748B]">Registro de todas as ações realizadas no sistema</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                <Input
                  placeholder="Buscar por usuário, ação..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-logs-input"
                />
              </div>
            </div>
            
            <div className="flex gap-2">
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="h-10 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm"
                data-testid="filter-action-select"
              >
                <option value="all">Todas as ações</option>
                {uniqueActions.map(action => (
                  <option key={action} value={action}>
                    {getActionConfig(action).label}
                  </option>
                ))}
              </select>
              
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="h-10 px-3 rounded-md border border-[#D1D5DB] bg-white text-sm"
                data-testid="filter-user-select"
              >
                <option value="all">Todos os usuários</option>
                {uniqueUsers.map(user => (
                  <option key={user} value={user}>{user}</option>
                ))}
              </select>
              
              <Button
                variant="outline"
                onClick={loadLogs}
                data-testid="refresh-logs-button"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              <Button
                variant="outline"
                onClick={exportLogs}
                data-testid="export-logs-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-4 text-center">
            <div className="text-2xl font-bold text-[#0F766E]">{logs.length}</div>
            <div className="text-xs text-[#64748B]">Total de Registros</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{uniqueUsers.length}</div>
            <div className="text-xs text-[#64748B]">Usuários Ativos</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{uniqueActions.length}</div>
            <div className="text-xs text-[#64748B]">Tipos de Ações</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{filteredLogs.length}</div>
            <div className="text-xs text-[#64748B]">Filtrados</div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white rounded-xl shadow-sm border border-[#D1D5DB] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F4F7F8]">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Data/Hora</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Usuário</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Ação</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Tipo</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">Detalhes</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#64748B]">IP</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[#64748B]">
                      Nenhum registro encontrado
                    </td>
                  </tr>
                ) : (
                  filteredLogs.slice(0, 100).map((log, index) => {
                    const config = getActionConfig(log.action);
                    const Icon = config.icon;
                    
                    return (
                      <tr key={log.id || index} className="border-b border-[#F4F7F8] hover:bg-[#F4F7F8]">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-[#64748B]" />
                            {formatTimestamp(log.timestamp)}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#0F766E]/10 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-[#0F766E]" />
                            </div>
                            <span className="text-sm font-medium text-[#1F2933]">
                              {log.user_name || 'Sistema'}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.color}`}>
                            <Icon className="w-3 h-3" />
                            {config.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#64748B]">
                          {log.entity_type || '-'}
                        </td>
                        <td className="py-3 px-4">
                          <code className="text-xs bg-[#F4F7F8] px-2 py-1 rounded max-w-xs truncate block">
                            {log.entity_id ? `ID: ${log.entity_id.substring(0, 8)}...` : 
                             log.details ? JSON.stringify(log.details).substring(0, 30) : '-'}
                          </code>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#64748B]">
                          {log.ip_address || '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          
          {filteredLogs.length > 100 && (
            <div className="p-4 text-center text-sm text-[#64748B] border-t border-[#D1D5DB]">
              Mostrando 100 de {filteredLogs.length} registros
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
